<template>
	<v-card>
		<v-card-title class="d-flex align-center flex-wrap">
			<v-icon :icon="side === 'left' ? 'mdi-arrow-left-bold' : 'mdi-arrow-right-bold'" class="mr-2" />
			<span class="text-truncate">{{ name }}</span>
			<v-spacer />
			<v-chip v-if="entries.length === 0" size="x-small" color="grey">no entries</v-chip>
			<v-chip v-else size="x-small" :color="scheduleInEffect ? 'primary' : 'grey'">
				{{ scheduleInEffect ? "active" : "schedule" }}: {{ activeTarget != null ? activeTarget + "°F" : "—" }}
			</v-chip>
		</v-card-title>

		<!-- Phone: stacked cards per entry -->
		<v-card-text v-if="display.xs.value" class="px-2 py-1">
			<div v-for="entry in entries" :key="entry.id" class="bed-sched-row">
				<div class="d-flex align-center">
					<v-switch v-model="entry.enabled" hide-details density="compact" inset color="primary" @change="save(entry)" class="flex-grow-0 mr-1" />
					<v-text-field v-model="entry.startTime" type="time" density="compact" hide-details variant="outlined" style="max-width: 110px" @blur="save(entry)" />
					<v-text-field v-model.number="entry.targetTemp" type="number" :min="55" :max="110" :step="0.5" density="compact" hide-details variant="outlined" suffix="°F" class="ml-2" style="max-width: 110px" @blur="save(entry)" />
					<v-spacer />
					<v-btn icon="mdi-delete" size="x-small" variant="text" :loading="removing === entry.id" @click="remove(entry.id)" />
				</div>
				<v-text-field v-model="entry.label" placeholder="label (optional)" density="compact" hide-details variant="plain" class="mt-1" @blur="save(entry)" />
				<v-divider class="my-2" />
			</div>

			<div class="bed-sched-add">
				<div class="d-flex align-center">
					<v-text-field v-model="newEntry.startTime" type="time" density="compact" hide-details variant="outlined" style="max-width: 110px" />
					<v-text-field v-model.number="newEntry.targetTemp" type="number" :min="55" :max="110" :step="0.5" placeholder="65" density="compact" hide-details variant="outlined" suffix="°F" class="ml-2" style="max-width: 110px" />
					<v-spacer />
					<v-btn color="primary" prepend-icon="mdi-plus" variant="elevated" :disabled="!canAdd" :loading="store.saving" @click="add">Add entry</v-btn>
				</div>
				<v-text-field v-model="newEntry.label" placeholder="label (optional)" density="compact" hide-details variant="plain" class="mt-1" />
			</div>
		</v-card-text>

		<!-- Tablet+: full table -->
		<v-card-text v-else class="px-2">
			<v-table density="compact" class="bed-sched-table">
				<thead>
					<tr>
						<th class="col-enabled">on</th>
						<th class="col-time">time</th>
						<th class="col-temp">target °F</th>
						<th class="col-label">label</th>
						<th class="col-action"></th>
					</tr>
				</thead>
				<tbody>
					<tr v-for="entry in entries" :key="entry.id">
						<td>
							<v-switch v-model="entry.enabled" hide-details density="compact" inset color="primary" @change="save(entry)" />
						</td>
						<td>
							<v-text-field v-model="entry.startTime" type="time" density="compact" hide-details variant="plain" @blur="save(entry)" />
						</td>
						<td>
							<v-text-field v-model.number="entry.targetTemp" type="number" :min="55" :max="110" :step="0.5" density="compact" hide-details variant="plain" @blur="save(entry)" />
						</td>
						<td>
							<v-text-field v-model="entry.label" placeholder="e.g. warm to sleep" density="compact" hide-details variant="plain" @blur="save(entry)" />
						</td>
						<td>
							<v-btn icon="mdi-delete" size="x-small" variant="text" :loading="removing === entry.id" @click="remove(entry.id)" />
						</td>
					</tr>
					<tr class="bed-sched-add-row">
						<td>
							<!-- enable switch is omitted here — the entry is enabled by default once added -->
						</td>
						<td>
							<v-text-field v-model="newEntry.startTime" type="time" density="compact" hide-details variant="outlined" />
						</td>
						<td>
							<v-text-field v-model.number="newEntry.targetTemp" type="number" :min="55" :max="110" :step="0.5" placeholder="65" density="compact" hide-details variant="outlined" />
						</td>
						<td>
							<v-text-field v-model="newEntry.label" placeholder="label" density="compact" hide-details variant="outlined" />
						</td>
						<td>
							<v-btn color="primary" prepend-icon="mdi-plus" variant="elevated" :disabled="!canAdd" :loading="store.saving" @click="add">Add</v-btn>
						</td>
					</tr>
				</tbody>
			</v-table>
		</v-card-text>
	</v-card>
</template>

<script setup>
import { computed, ref } from "vue";
import { useDisplay } from "vuetify";
import schedulesStore from "../stores/schedulesStore.js";
import statusStore from "../stores/statusStore.js";
import { useSideName } from "../composables/useSideName.js";
import { resolveScheduledTarget } from "../utils/scheduleResolver.js";

var props = defineProps({
	side: { type: String, required: true },
});

var store = schedulesStore.data;
var name = useSideName(() => props.side);
var display = useDisplay();

var entries = computed(() => store.bySide?.[props.side] ?? []);
var activeTarget = computed(() => resolveScheduledTarget(Date.now(), entries.value));

// True when the schedule's resolved target is what's actually driving the dock —
// auto mode, no manual hold / pump snooze / auto-vacate. Otherwise the chip shows
// what the schedule says but greyed, since something else is in control.
var scheduleInEffect = computed(() => {
	var st = statusStore.data.data?.sides?.[props.side];
	if (!st) { return false; }
	if (st.mode !== "auto") { return false; }
	if (st.holdUntil != null) { return false; }
	if (st.pumpSnoozedTarget != null) { return false; }
	if (st.autoVacatedAt != null) { return false; }
	if (st.offWindowUntil != null) { return false; }
	return true;
});

var newEntry = ref({ enabled: true, startTime: "22:00", targetTemp: null, label: "" });

var canAdd = computed(() => {
	return /^\d{1,2}:\d{2}$/.test(newEntry.value.startTime ?? "")
		&& Number.isFinite(Number(newEntry.value.targetTemp))
		&& newEntry.value.targetTemp >= 55
		&& newEntry.value.targetTemp <= 110;
});

async function add() {
	await schedulesStore.methods.create(props.side, {
		enabled: newEntry.value.enabled,
		startTime: newEntry.value.startTime,
		targetTemp: Number(newEntry.value.targetTemp),
		label: newEntry.value.label || null,
	});
	newEntry.value = { enabled: true, startTime: "22:00", targetTemp: null, label: "" };
}

async function save(entry) {
	if (!/^\d{1,2}:\d{2}$/.test(entry.startTime)) { return; }
	if (!Number.isFinite(Number(entry.targetTemp))) { return; }
	await schedulesStore.methods.update(entry.id, {
		enabled: entry.enabled,
		startTime: entry.startTime,
		targetTemp: Number(entry.targetTemp),
		label: entry.label || null,
	});
}

var removing = ref(null);
async function remove(id) {
	removing.value = id;
	try { await schedulesStore.methods.remove(id); }
	finally { removing.value = null; }
}
</script>

<style scoped>
.bed-sched-row,
.bed-sched-add {
	margin-bottom: 4px;
}

.bed-sched-table .col-enabled { width: 70px; }
.bed-sched-table .col-time    { width: 160px; }
.bed-sched-table .col-temp    { width: 140px; }
.bed-sched-table .col-action  { width: 120px; }

.bed-sched-table td {
	padding-top: 4px !important;
	padding-bottom: 4px !important;
	vertical-align: middle;
}

.bed-sched-add-row td {
	padding-top: 8px !important;
	padding-bottom: 8px !important;
}

.bed-sched-table :deep(.v-field--variant-outlined .v-field__field) {
	min-height: 36px;
}
</style>
